import React, { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, CheckCircle2, Loader2, Upload, User } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../../shared/components/ui/Modal';
import {
  createClosedDealAttachment,
  getCloseDeals,
  uploadFilesToS3
} from '../services/api/implementation/impl';

const SalesDominoDealDocumentModal = ({ task, onClose, onComplete }) => {
  const modalFileInputRef = useRef(null);
  const [documentName, setDocumentName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFileData, setUploadedFileData] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const projectId = task?.project_id ?? task?.project?.id ?? null;
  const unitId = task?.unit_id ?? task?.unit?.id ?? null;
  const unitName = task?.unit?.unitName ?? '—';

  const { data: dealsResponse, isLoading: isLoadingDeal } = useQuery({
    queryKey: ['closeDealsForDominoDoc', projectId],
    queryFn: () => getCloseDeals(projectId),
    enabled: !!projectId
  });

  const deal = useMemo(() => {
    const list = dealsResponse?.data ?? [];
    if (!unitId) return null;
    return list.find(
      (d) =>
        Number(d.unit_id) === Number(unitId) || Number(d.unit?.id) === Number(unitId)
    );
  }, [dealsResponse?.data, unitId]);

  const dealId = deal?.id ?? null;
  const leadName =
    deal?.lead?.user?.name ?? deal?.lead?.name ?? deal?.lead?.user_name ?? '—';

  const processFile = async (file) => {
    if (!file) return;

    setSelectedFile(file);
    if (!documentName.trim()) {
      setDocumentName(file.name);
    }

    setIsUploadingFile(true);
    try {
      const uploaded = await uploadFilesToS3([file]);
      if (!uploaded?.length) throw new Error('Upload failed');

      setUploadedFileData({
        url: uploaded[0].url,
        type: file.type || uploaded[0].type,
        size: file.size
      });
      toast.success('File uploaded');
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to upload file');
      setSelectedFile(null);
      setUploadedFileData(null);
      if (modalFileInputRef.current) modalFileInputRef.current.value = '';
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = documentName.trim();

    if (!dealId) {
      toast.error('Closed deal not found for this unit');
      return;
    }
    if (!trimmedName) {
      toast.error('Document name is required');
      return;
    }
    if (!uploadedFileData?.url) {
      toast.error('Please upload a file');
      return;
    }

    setIsSubmitting(true);
    try {
      await createClosedDealAttachment({
        deal_id: dealId,
        name: trimmedName,
        url: uploadedFileData.url,
        mime_type: uploadedFileData.type || selectedFile?.type || undefined,
        size_bytes: uploadedFileData.size ?? selectedFile?.size
      });
      toast.success('Document attached successfully');
      onComplete?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to attach document'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal name="Add document to closed deal" width={48} onClose={onClose}>
      <form onSubmit={handleSubmit} className="px-6 pb-6 pt-2">
        <p className="mb-4 text-sm text-slate-500">
          Attach a file for this closed deal. Uploading the first document completes this task.
        </p>

        <div className="mb-5 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3">
            <div className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
              <Building2 className="h-3.5 w-3.5" />
              Unit
            </div>
            <p className="text-sm font-semibold text-slate-900">{unitName}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3">
            <div className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
              <User className="h-3.5 w-3.5" />
              Lead
            </div>
            {isLoadingDeal ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : (
              <p className="text-sm font-semibold text-slate-900">{leadName}</p>
            )}
          </div>
        </div>

        {!isLoadingDeal && !dealId && (
          <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            No closed deal found for this unit. Close the deal first, then upload documents.
          </p>
        )}

        <div className="space-y-4">
          <div>
            <label
              htmlFor="domino-doc-name"
              className="mb-1.5 block text-sm font-medium text-slate-700">
              Document name <span className="text-red-500">*</span>
            </label>
            <input
              id="domino-doc-name"
              type="text"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              maxLength={255}
              placeholder="e.g. Signed agreement"
              disabled={isSubmitting || !dealId}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600/20 disabled:bg-slate-50"
            />
          </div>

          <div>
            <span className="mb-1.5 block text-sm font-medium text-slate-700">
              File <span className="text-red-500">*</span>
            </span>
            <input
              ref={modalFileInputRef}
              type="file"
              className="sr-only"
              disabled={isUploadingFile || isSubmitting || !dealId}
              onChange={(e) => processFile(e.target.files?.[0])}
            />
            <div
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && modalFileInputRef.current?.click()}
              onClick={() =>
                !isUploadingFile && !isSubmitting && dealId && modalFileInputRef.current?.click()
              }
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (dealId) processFile(e.dataTransfer.files?.[0]);
              }}
              className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-8 text-center transition ${
                uploadedFileData
                  ? 'border-emerald-200 bg-emerald-50/40'
                  : 'border-slate-200 bg-slate-50/50 hover:border-teal-300 hover:bg-teal-50/30'
              } ${!dealId || isUploadingFile || isSubmitting ? 'pointer-events-none opacity-60' : ''}`}>
              {isUploadingFile ? (
                <>
                  <Loader2 className="mb-2 h-8 w-8 animate-spin text-teal-700" />
                  <p className="text-sm font-medium text-slate-700">Uploading…</p>
                </>
              ) : uploadedFileData ? (
                <>
                  <CheckCircle2 className="mb-2 h-8 w-8 text-emerald-600" />
                  <p className="text-sm font-medium text-slate-800">
                    {selectedFile?.name || 'File ready'}
                  </p>
                </>
              ) : (
                <>
                  <Upload className="mb-2 h-8 w-8 text-teal-700" />
                  <p className="text-sm font-medium text-slate-800">Drag & drop or click to browse</p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3 border-t border-slate-100 pt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting || isUploadingFile}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || isUploadingFile || !uploadedFileData || !dealId}
            className="inline-flex min-w-[5.5rem] items-center justify-center gap-2 rounded-lg bg-[#2A565E] px-4 py-2 text-sm font-medium text-white hover:bg-[#234952] disabled:cursor-not-allowed disabled:opacity-50">
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {isSubmitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default SalesDominoDealDocumentModal;
